import type * as Astronomy from "astronomy-engine";
import { ECLIPSE_DATE, getLocalCircumstances, getSkyState, makeObserver } from "./eclipse";
import type { ContactEvent, LocalCircumstances } from "./eclipse";
import { drawSky } from "./render";

// Reykjavík — inside the path of totality — used until/unless the browser grants a real location.
const FALLBACK_LOCATION = { lat: 64.1466, lon: -21.9426, elevation: 0, label: "Reykjavík, Iceland (example)" };

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const canvas = $<HTMLCanvasElement>("sky");
const phaseLabelEl = $<HTMLDivElement>("phaseLabel");
const obscurationValueEl = $<HTMLSpanElement>("obscurationValue");
const countdownEl = $<HTMLDivElement>("countdown");
const countdownLabelEl = $<HTMLDivElement>("countdownLabel");
const timelineNowEl = $<HTMLDivElement>("timelineNow");
const contactListEl = $<HTMLOListElement>("contactList");
const locationTextEl = $<HTMLSpanElement>("locationText");
const locationBadgeEl = $<HTMLSpanElement>("locationBadge");
const manualBtn = $<HTMLButtonElement>("manualLocationBtn");
const resetLocationBtn = $<HTMLButtonElement>("resetLocationBtn");
const manualForm = $<HTMLFormElement>("manualLocationForm");
const latInput = $<HTMLInputElement>("latInput");
const lonInput = $<HTMLInputElement>("lonInput");
const elevInput = $<HTMLInputElement>("elevInput");

const devToggle = $<HTMLButtonElement>("devToggle");
const devPanel = $<HTMLDivElement>("devPanel");
const devSlider = $<HTMLInputElement>("devSlider");
const devTimeEl = $<HTMLSpanElement>("devTime");
const devPlayBtn = $<HTMLButtonElement>("devPlay");
const devSpeedSel = $<HTMLSelectElement>("devSpeed");
const devLiveBtn = $<HTMLButtonElement>("devLive");

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// All displayed clock times are UK local time (GMT/BST), regardless of device timezone.
const UK_TZ = "Europe/London";
const timeFmt = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: UK_TZ });
const dateTimeFmt = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: UK_TZ,
});

// --- Time source: real clock, or a scrubbed value for testing ------------

let overrideMs: number | null = null;
let playing = false;
let playAnchorReal = 0;
let playAnchorSim = 0;

function getNow(): Date {
  if (overrideMs === null) return new Date();
  if (playing) {
    const speed = Number(devSpeedSel.value);
    const elapsedReal = performance.now() - playAnchorReal;
    return new Date(playAnchorSim + elapsedReal * speed);
  }
  return new Date(overrideMs);
}

// --- Manual location persistence --------------------------------------------
//
// A manually-entered location is saved to localStorage and takes over completely:
// once set, we never touch the GPS again on our own. This is deliberate — a flaky
// or wrong GPS fix shouldn't silently override a location the user picked themselves.
// The only way back to GPS is the explicit "Reset to GPS location" button.

const STORAGE_KEY = "eclipse-timer:manual-location";

interface SavedLocation {
  lat: number;
  lon: number;
  elevation: number;
}

function loadSavedLocation(): SavedLocation | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.lat === "number" && typeof parsed.lon === "number") {
      return { lat: parsed.lat, lon: parsed.lon, elevation: typeof parsed.elevation === "number" ? parsed.elevation : 0 };
    }
  } catch {
    // localStorage disabled (e.g. private browsing) or the stored value is corrupt — ignore it.
  }
  return null;
}

function saveManualLocation(loc: SavedLocation): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
  } catch {
    // Storage full or disabled — the manual location still works for this session.
  }
}

function clearSavedLocation(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do if storage is unavailable.
  }
}

// --- App state -------------------------------------------------------------

let observer: Astronomy.Observer = makeObserver(FALLBACK_LOCATION.lat, FALLBACK_LOCATION.lon, FALLBACK_LOCATION.elevation);
let circumstances: LocalCircumstances = getLocalCircumstances(observer);
let sliderRangeStart = 0;
let sliderRangeEnd = 0;

// Guards against a slow reverse-geocode lookup from an earlier location landing after
// the user has already moved on to a different one.
let locationRequestId = 0;

function initLocation(lat: number, lon: number, elevation: number, label: string | null, isManual: boolean): void {
  const requestId = ++locationRequestId;

  observer = makeObserver(lat, lon, elevation);
  circumstances = getLocalCircumstances(observer);

  const coordText = `${formatCoord(lat, "N", "S")}, ${formatCoord(lon, "E", "W")} · ${Math.round(elevation)} m elevation`;
  locationTextEl.textContent = label ?? coordText;
  locationBadgeEl.classList.toggle("hidden", !isManual);
  resetLocationBtn.classList.toggle("hidden", !isManual);

  renderContactList(circumstances);
  setupDevRange(circumstances);

  // Only bother looking up a town for a real coordinate reading, not for canned labels
  // like "locating you…" or the Reykjavík fallback, which are already human-readable.
  if (label === null) {
    findNearestTown(lat, lon).then((town) => {
      if (!town || requestId !== locationRequestId) return;
      locationTextEl.textContent = `${coordText} — near ${town}`;
    });
  }
}

function formatCoord(v: number, pos: string, neg: string): string {
  return `${Math.abs(v).toFixed(4)}°${v >= 0 ? pos : neg}`;
}

// Nearest-town lookup so people can eyeball "does this look like where I am" rather than
// trusting a raw lat/lon — done entirely against a small offline gazetteer bundled with the
// app (public/towns.json, generated by scripts/generate-towns.mjs) instead of a third-party
// API, so no coordinates ever leave the device. It covers the UK, Iceland, Spain, Portugal
// and Greenland — the countries the 12 Aug 2026 eclipse's totality path and strongest
// partial phase cross. Best-effort only: any failure just leaves the coordinates on their
// own, which is still a perfectly usable location.

type Town = [name: string, lat: number, lon: number];

let townsPromise: Promise<Town[]> | null = null;

function loadTowns(): Promise<Town[]> {
  if (!townsPromise) {
    townsPromise = fetch("/towns.json")
      .then((res) => (res.ok ? (res.json() as Promise<Town[]>) : []))
      .catch(() => []);
  }
  return townsPromise;
}

const EARTH_RADIUS_KM = 6371;

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
}

// If the nearest entry is implausibly far away, the visitor is probably outside the
// gazetteer's coverage — say nothing rather than point at a misleadingly "nearest" town.
const MAX_TOWN_DISTANCE_KM = 60;

async function findNearestTown(lat: number, lon: number): Promise<string | null> {
  const towns = await loadTowns();
  let best: Town | null = null;
  let bestDist = Infinity;
  for (const town of towns) {
    const d = haversineKm(lat, lon, town[1], town[2]);
    if (d < bestDist) {
      bestDist = d;
      best = town;
    }
  }
  return best && bestDist <= MAX_TOWN_DISTANCE_KM ? best[0] : null;
}

// --- Geolocation -------------------------------------------------------------

function locateWithGps(): void {
  initLocation(
    FALLBACK_LOCATION.lat,
    FALLBACK_LOCATION.lon,
    FALLBACK_LOCATION.elevation,
    `Showing ${FALLBACK_LOCATION.label} — locating you…`,
    false
  );

  if (!("geolocation" in navigator)) {
    initLocation(
      FALLBACK_LOCATION.lat,
      FALLBACK_LOCATION.lon,
      FALLBACK_LOCATION.elevation,
      `Geolocation unavailable — showing ${FALLBACK_LOCATION.label}`,
      false
    );
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { latitude, longitude, altitude } = pos.coords;
      initLocation(latitude, longitude, altitude ?? 0, null, false);
    },
    () => {
      initLocation(
        FALLBACK_LOCATION.lat,
        FALLBACK_LOCATION.lon,
        FALLBACK_LOCATION.elevation,
        `Location access denied — showing ${FALLBACK_LOCATION.label}`,
        false
      );
    },
    { enableHighAccuracy: true, timeout: 10_000 }
  );
}

// A saved manual location wins outright — we don't touch the GPS at all until reset.
const savedLocation = loadSavedLocation();
if (savedLocation) {
  initLocation(savedLocation.lat, savedLocation.lon, savedLocation.elevation, null, true);
} else {
  locateWithGps();
}

manualBtn.addEventListener("click", () => manualForm.classList.toggle("hidden"));
manualForm.addEventListener("submit", (e) => {
  e.preventDefault();
  const lat = Number(latInput.value);
  const lon = Number(lonInput.value);
  const elev = elevInput.value ? Number(elevInput.value) : 0;
  if (Number.isNaN(lat) || Number.isNaN(lon)) return;
  manualForm.classList.add("hidden");
  saveManualLocation({ lat, lon, elevation: elev });
  initLocation(lat, lon, elev, null, true);
});

resetLocationBtn.addEventListener("click", () => {
  clearSavedLocation();
  locateWithGps();
});

// --- Contact list / timeline -------------------------------------------------

function renderContactList(circ: LocalCircumstances): void {
  contactListEl.innerHTML = "";

  if (!circ.visible) {
    const li = document.createElement("li");
    li.style.gridColumn = "1 / -1";
    li.textContent = circ.nextElsewhere
      ? `No eclipse visible here on 12 Aug 2026. Your next one: ${circ.nextElsewhere.kind} eclipse around ${dateTimeFmt.format(circ.nextElsewhere.time)} UK time.`
      : "No eclipse visible from this location on 12 Aug 2026.";
    contactListEl.appendChild(li);
    return;
  }

  const rows: [string, ContactEvent | null][] = [
    ["C1 · Partial begins", circ.c1],
    [circ.kind === "annular" ? "C2 · Annularity begins" : "C2 · Totality begins", circ.c2],
    ["Maximum eclipse", circ.max],
    [circ.kind === "annular" ? "C3 · Annularity ends" : "C3 · Totality ends", circ.c3],
    ["C4 · Partial ends", circ.c4],
  ];

  for (const [name, event] of rows) {
    if (!event) continue;
    const nameEl = document.createElement("li");
    nameEl.className = "contact-name";
    nameEl.dataset.iso = event.time.toISOString();
    nameEl.textContent = name;
    const timeEl = document.createElement("li");
    timeEl.className = "contact-time";
    timeEl.textContent = timeFmt.format(event.time);
    contactListEl.append(nameEl, timeEl);
  }
}

function setupDevRange(circ: LocalCircumstances): void {
  if (circ.visible && circ.c1 && circ.c4) {
    sliderRangeStart = circ.c1.time.getTime() - 30 * 60_000;
    sliderRangeEnd = circ.c4.time.getTime() + 30 * 60_000;
  } else {
    sliderRangeStart = ECLIPSE_DATE.getTime();
    sliderRangeEnd = ECLIPSE_DATE.getTime() + 24 * 3600_000;
  }
  devSlider.min = "0";
  devSlider.max = "10000";
  const now = Date.now();
  const clamped = Math.min(Math.max(now, sliderRangeStart), sliderRangeEnd);
  devSlider.value = String(Math.round(((clamped - sliderRangeStart) / (sliderRangeEnd - sliderRangeStart)) * 10000));
}

// --- Dev time-scrubber -------------------------------------------------------

devToggle.addEventListener("click", () => devPanel.classList.toggle("hidden"));

devSlider.addEventListener("input", () => {
  playing = false;
  devPlayBtn.textContent = "▶";
  const frac = Number(devSlider.value) / 10000;
  overrideMs = sliderRangeStart + frac * (sliderRangeEnd - sliderRangeStart);
});

devPlayBtn.addEventListener("click", () => {
  playing = !playing;
  devPlayBtn.textContent = playing ? "⏸" : "▶";
  if (playing) {
    if (overrideMs === null) overrideMs = sliderRangeStart;
    playAnchorReal = performance.now();
    playAnchorSim = overrideMs;
  } else {
    overrideMs = getNow().getTime();
  }
});

devLiveBtn.addEventListener("click", () => {
  overrideMs = null;
  playing = false;
  devPlayBtn.textContent = "▶";
});

// --- Formatting helpers -------------------------------------------------------

function formatCountdown(ms: number): string {
  const sign = ms < 0 ? "+" : "-";
  ms = Math.abs(ms);
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (days > 0) return `${sign}${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${sign}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

interface Phase {
  label: string;
  totality: boolean;
  countdownTarget: Date | null;
  countdownLabel: string;
  /** ISO time of the contact-list row to highlight as current, if any. */
  highlightIso: string | null;
}

/**
 * How long, either side of the true instant of maximum eclipse, counts as "at maximum"
 * for display purposes — the percentage covered barely changes in this window, so treating
 * it as an instant reads as a glitch (partial eclipse skipping straight past it).
 */
function maxEclipseHalfWindowMs(c1: Date, c4: Date): number {
  const totalPartialMs = c4.getTime() - c1.getTime();
  return Math.min(180_000, Math.max(60_000, 0.05 * totalPartialMs));
}

function getPhase(circ: LocalCircumstances, now: Date): Phase {
  if (!circ.visible || !circ.c1 || !circ.c4 || !circ.max) {
    return { label: "Not visible here", totality: false, countdownTarget: null, countdownLabel: "", highlightIso: null };
  }
  const t = now.getTime();
  const totalWord = circ.kind === "annular" ? "annularity" : "totality";

  if (t < circ.c1.time.getTime()) {
    return { label: "Before eclipse", totality: false, countdownTarget: circ.c1.time, countdownLabel: "until first contact", highlightIso: circ.c1.time.toISOString() };
  }

  if (circ.c2 && circ.c3) {
    if (t < circ.c2.time.getTime()) {
      return { label: "Partial eclipse", totality: false, countdownTarget: circ.c2.time, countdownLabel: `until ${totalWord}`, highlightIso: circ.c2.time.toISOString() };
    }
    if (t < circ.c3.time.getTime()) {
      return {
        label: circ.kind === "annular" ? "Annularity" : "Totality",
        totality: true,
        countdownTarget: circ.c3.time,
        countdownLabel: `until end of ${totalWord}`,
        highlightIso: circ.max.time.toISOString(),
      };
    }
    if (t < circ.c4.time.getTime()) {
      return { label: "Partial eclipse", totality: false, countdownTarget: circ.c4.time, countdownLabel: "until last contact", highlightIso: circ.c4.time.toISOString() };
    }
    return { label: "Eclipse ended", totality: false, countdownTarget: circ.c4.time, countdownLabel: "since eclipse ended", highlightIso: null };
  }

  // Partial-only eclipse: "maximum eclipse" is a short window around the peak, not an
  // instant, so there's a real "at maximum" state between growing and waning partial phases.
  const halfWindow = maxEclipseHalfWindowMs(circ.c1.time, circ.c4.time);
  const windowStart = circ.max.time.getTime() - halfWindow;
  const windowEnd = circ.max.time.getTime() + halfWindow;

  if (t < windowStart) {
    return { label: "Partial eclipse", totality: false, countdownTarget: circ.max.time, countdownLabel: "until maximum eclipse", highlightIso: circ.max.time.toISOString() };
  }
  if (t < windowEnd) {
    return { label: "Maximum eclipse", totality: false, countdownTarget: new Date(windowEnd), countdownLabel: "until partial eclipse resumes", highlightIso: circ.max.time.toISOString() };
  }
  if (t < circ.c4.time.getTime()) {
    return { label: "Partial eclipse", totality: false, countdownTarget: circ.c4.time, countdownLabel: "until last contact", highlightIso: circ.c4.time.toISOString() };
  }
  return { label: "Eclipse ended", totality: false, countdownTarget: circ.c4.time, countdownLabel: "since eclipse ended", highlightIso: null };
}

// --- Render loop -------------------------------------------------------------

let lastCalc = 0;

function tick(tMs: number): void {
  requestAnimationFrame(tick);
  if (tMs - lastCalc < 200) return;
  lastCalc = tMs;

  const now = getNow();
  const sky = getSkyState(observer, now);
  drawSky(canvas, sky, reducedMotion ? 0 : tMs / 1000);

  obscurationValueEl.textContent = sky.obscurationPercent.toFixed(1);

  const phase = getPhase(circumstances, now);
  phaseLabelEl.textContent = phase.label.toUpperCase();
  phaseLabelEl.classList.toggle("totality", phase.totality);

  if (phase.countdownTarget) {
    countdownEl.textContent = formatCountdown(phase.countdownTarget.getTime() - now.getTime());
    countdownLabelEl.textContent = phase.countdownLabel;
  } else {
    countdownEl.textContent = "—";
    countdownLabelEl.textContent = "";
  }

  if (sliderRangeEnd > sliderRangeStart) {
    const frac = Math.min(1, Math.max(0, (now.getTime() - sliderRangeStart) / (sliderRangeEnd - sliderRangeStart)));
    timelineNowEl.style.left = `${frac * 100}%`;
  }

  for (const el of contactListEl.querySelectorAll<HTMLLIElement>(".contact-name")) {
    el.classList.toggle("current", el.dataset.iso === phase.highlightIso);
  }

  devTimeEl.textContent = overrideMs !== null ? `${dateTimeFmt.format(now)} UK` : "live";
}

requestAnimationFrame(tick);
